<template>
  <div>
    <v-card outlined raised>
      <v-card-title>
        Brokers Requests ({{ addTSBVZ(totalRecords) }})
        <v-spacer></v-spacer>
        <v-select
            v-model="typeFilter"
            :items="typeOptions"
            label="Type"
            dense
            outlined
            hide-details
            clearable
            class="mr-3"
            style="max-width: 200px;"
            @change="refreshData(true)"
        />
        <v-select
            v-model="statusFilter"
            :items="statusOptions"
            label="Status"
            dense
            outlined
            hide-details
            clearable
            class="mr-3"
            style="max-width: 160px;"
            @change="refreshData(true)"
        />
        <v-text-field
            v-model="searchQuery"
            append-icon="mdi-magnify"
            label="Search"
            single-line
            hide-details
            clearable
            style="max-width: 260px;"
            @keyup.enter="refreshData(true)"
            @click:clear="refreshData(true)"
        />
      </v-card-title>

      <v-data-table
          :headers="headers"
          :items="items"
          :options.sync="pagination"
          :server-items-length="totalRecords"
          :footer-props="{ itemsPerPageOptions: rowsPerPageItems, showFirstLastPage: true }"
          :loading="loading"
          @update:options="onOptionsUpdate"
          class="elevation-0"
      >
        <template v-slot:[`item.request_type_label`]="{ item }">
          {{ item.request_type_label || formatType(item.request_type) }}
        </template>
        <template v-slot:[`item.submission_type_label`]="{ item }">
          <v-chip
              x-small
              label
              :color="item.submission_type === 'edit' ? 'indigo' : 'blue-grey'"
              text-color="white"
          >
            {{ item.submission_type_label || (item.submission_type === 'edit' ? 'Edit' : 'Create') }}
          </v-chip>
        </template>
        <template v-slot:[`item.status`]="{ item }">
          <v-chip
              x-small
              label
              :color="statusColor(item.status)"
              text-color="white"
          >
            {{ item.status_label || formatStatus(item.status) }}
          </v-chip>
        </template>
        <template v-slot:[`item.broker_name`]="{ item }">
          <div>{{ item.broker_name || "—" }}</div>
          <div class="caption text--secondary">{{ item.broker_email || "" }}</div>
        </template>
        <template v-slot:[`item.created_at`]="{ item }">
          {{ formatDate(item.created_at) }}
        </template>
        <template v-slot:[`item.actions`]="{ item }">
          <v-btn
              color="#00004d"
              dark
              small
              @click="openReview(item)"
          >
            Review
          </v-btn>
        </template>
      </v-data-table>
    </v-card>

    <v-dialog v-model="reviewDialog" max-width="960px" scrollable>
      <v-card>
        <v-card-title>
          Review Broker Request
          <v-spacer></v-spacer>
          <v-chip
              v-if="detailStatus"
              small
              label
              class="mr-3"
              :color="statusColor(detailStatus)"
              text-color="white"
          >
            {{ detailStatusLabel }}
          </v-chip>
          <v-btn
              v-if="canApprove"
              color="success"
              dark
              small
              class="mr-2"
              :loading="approving"
              :disabled="detailLoading || rejecting || approving"
              @click="openApproveDialog"
          >
            Approve
          </v-btn>
          <v-btn
              v-if="canReject"
              color="error"
              dark
              small
              class="mr-2"
              :disabled="detailLoading || rejecting || approving"
              @click="openRejectDialog"
          >
            Reject
          </v-btn>
          <v-btn icon :disabled="detailLoading" @click="reviewDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text>
          <div v-if="detailLoading" class="text-center py-8">
            <v-progress-circular indeterminate color="primary" />
          </div>
          <div v-else-if="detail">
            <h3 class="subtitle-1 font-weight-medium mb-2">
              {{ detail.request_type_label }} — {{ requestTitle }}
              <v-chip
                  v-if="detail.submission_type_label"
                  class="ml-2"
                  x-small
                  label
                  :color="detail.submission_type === 'edit' ? 'indigo' : 'blue-grey'"
                  text-color="white"
              >
                {{ detail.submission_type_label }}
              </v-chip>
            </h3>

            <v-simple-table dense class="mb-6">
              <tbody>
              <tr>
                <td width="30%"><strong>Broker</strong></td>
                <td>{{ (detail.broker && detail.broker.name) || "—" }}</td>
              </tr>
              <tr>
                <td><strong>Email</strong></td>
                <td>{{ (detail.broker && detail.broker.email) || "—" }}</td>
              </tr>
              <tr>
                <td><strong>Agency</strong></td>
                <td>{{ (detail.broker && detail.broker.agency_name) || "—" }}</td>
              </tr>
              <tr>
                <td><strong>Phone</strong></td>
                <td>{{ (detail.broker && detail.broker.number) || "—" }}</td>
              </tr>
              <tr>
                <td><strong>Submitted</strong></td>
                <td>{{ formatDate(detail.request && detail.request.created_at) }}</td>
              </tr>
              <tr v-if="detail.request && detail.request.reviewed_at">
                <td><strong>Reviewed</strong></td>
                <td>{{ formatDate(detail.request.reviewed_at) }}</td>
              </tr>
              </tbody>
            </v-simple-table>

            <!-- Edit requests only: show what changed -->
            <template v-if="isEditRequest">
              <h3 class="subtitle-1 font-weight-medium mb-2">
                Changed fields
                <v-chip
                    v-if="changeCount > 0"
                    class="ml-2"
                    x-small
                    label
                    color="orange darken-2"
                    text-color="white"
                >
                  {{ changeCount }}
                </v-chip>
              </h3>
              <v-alert
                  v-if="changeCount < 1"
                  type="info"
                  dense
                  text
                  class="mb-6"
              >
                No field differences detected against the baseline / live catalog.
              </v-alert>
              <div v-else class="mb-6">
                <div class="mb-3">
                  <v-chip
                      v-for="tab in changedTabs"
                      :key="'tab-' + tab.key"
                      class="mr-2 mb-2"
                      small
                      label
                      :outlined="activeChangeTab !== tab.key"
                      :color="activeChangeTab === tab.key ? 'indigo' : 'grey'"
                      :text-color="activeChangeTab === tab.key ? 'white' : undefined"
                      @click="activeChangeTab = tab.key"
                  >
                    {{ tab.label }} ({{ tab.change_count }})
                  </v-chip>
                </div>
                <v-card
                    v-for="change in changesForActiveTab"
                    :key="change.field + '-' + change.tab"
                    outlined
                    class="pa-3 mb-3"
                >
                  <div class="text-body-2 font-weight-medium mb-2">{{ change.label }}</div>
                  <v-row dense>
                    <v-col cols="12" sm="6">
                      <div class="caption text--secondary mb-1">Current / baseline</div>
                      <div
                          class="pa-2"
                          style="background:#fafafa;border:1px solid #e0e0e0;border-radius:4px;text-decoration:line-through;color:#757575;white-space:pre-wrap;word-break:break-word;"
                      >
                        {{ change.old_display || "—" }}
                      </div>
                    </v-col>
                    <v-col cols="12" sm="6">
                      <div class="caption text--secondary mb-1">Proposed by broker</div>
                      <div
                          class="pa-2"
                          style="background:#f3faf5;border:1px solid #cce8d4;border-radius:4px;color:#166534;font-weight:600;white-space:pre-wrap;word-break:break-word;"
                      >
                        {{ change.new_display || "—" }}
                      </div>
                    </v-col>
                  </v-row>
                </v-card>
              </div>
            </template>

            <h3 class="subtitle-1 font-weight-medium mb-2">Request details</h3>
            <v-simple-table dense class="mb-6">
              <tbody>
              <tr v-for="row in detailRows" :key="row.label">
                <td width="30%"><strong>{{ row.label }}</strong></td>
                <td>{{ row.value }}</td>
              </tr>
              </tbody>
            </v-simple-table>

            <div v-if="detail.request && detail.request.description" class="mb-6">
              <h3 class="subtitle-1 font-weight-medium mb-2">Description</h3>
              <div class="pa-3" style="border: 1px solid #e0e0e0; border-radius: 4px;" v-html="sanitizeRichHtml(detail.request.description)"></div>
            </div>

            <div v-if="detail.request && detail.request.admin_notes && detailStatus === 'rejected'" class="mb-6">
              <h3 class="subtitle-1 font-weight-medium mb-2">Rejection Reason</h3>
              <div class="pa-3" style="border: 1px solid #f1c0c0; border-radius: 4px; background: #fff5f5;">
                {{ detail.request.admin_notes }}
              </div>
            </div>

            <h3 class="subtitle-1 font-weight-medium mb-3">Files</h3>
            <p v-if="!detail.files || detail.files.length === 0" class="text--secondary mb-0">
              No files uploaded.
            </p>
            <v-row v-else dense>
              <v-col
                  v-for="file in detail.files"
                  :key="file.id"
                  cols="12"
                  sm="6"
              >
                <v-card outlined class="pa-3 fill-height">
                  <div class="d-flex justify-space-between align-center">
                    <div>
                      <div class="text-body-2 font-weight-medium">{{ file.original_name || (file.title + (file.extension || '')) }}</div>
                      <div class="caption text--secondary">{{ file.module_title || "—" }}</div>
                    </div>
                    <v-btn
                        v-if="file.public_url"
                        small
                        color="primary"
                        outlined
                        :href="file.public_url"
                        target="_blank"
                        rel="noopener"
                    >
                      <v-icon left small>mdi-open-in-new</v-icon>
                      View
                    </v-btn>
                  </div>
                </v-card>
              </v-col>
            </v-row>

            <template v-if="detail.source === 'project'">
              <h3 class="subtitle-1 font-weight-medium mb-3 mt-6">Typical Floor Plans</h3>
              <p v-if="!detail.typical_floor_plans || detail.typical_floor_plans.length === 0" class="text--secondary mb-0">
                No typical floor plans.
              </p>
              <v-card
                  v-for="plan in detail.typical_floor_plans"
                  :key="'tfp-' + plan.id"
                  outlined
                  class="pa-4 mb-4"
              >
                <div class="text-body-1 font-weight-medium mb-2">{{ plan.title || "—" }}</div>
                <v-simple-table dense class="mb-3">
                  <tbody>
                  <tr>
                    <td width="30%"><strong>Floor Level</strong></td>
                    <td>{{ plan.floor_level || "—" }}</td>
                  </tr>
                  <tr>
                    <td><strong>From Level</strong></td>
                    <td>{{ plan.floor_from || "—" }}</td>
                  </tr>
                  <tr>
                    <td><strong>To Level</strong></td>
                    <td>{{ plan.floor_to || "—" }}</td>
                  </tr>
                  </tbody>
                </v-simple-table>
                <div v-if="plan.description" class="mb-3">
                  <div class="caption text--secondary mb-1">Description</div>
                  <div class="pa-3" style="border: 1px solid #e0e0e0; border-radius: 4px;" v-html="sanitizeRichHtml(plan.description)"></div>
                </div>
                <div class="caption text--secondary mb-2">Files</div>
                <p v-if="!plan.files || plan.files.length === 0" class="text--secondary mb-0">No files.</p>
                <v-row v-else dense>
                  <v-col v-for="file in plan.files" :key="file.id" cols="12" sm="6">
                    <v-card outlined class="pa-3">
                      <div class="d-flex justify-space-between align-center">
                        <div>
                          <div class="text-body-2 font-weight-medium">{{ file.original_name || (file.title + (file.extension || '')) }}</div>
                          <div class="caption text--secondary">{{ file.module_title || "—" }}</div>
                        </div>
                        <v-btn
                            v-if="file.public_url"
                            small
                            color="primary"
                            outlined
                            :href="file.public_url"
                            target="_blank"
                            rel="noopener"
                        >
                          View
                        </v-btn>
                      </div>
                    </v-card>
                  </v-col>
                </v-row>
              </v-card>

              <h3 class="subtitle-1 font-weight-medium mb-3 mt-2">Internal Layouts</h3>
              <p v-if="!detail.internal_layouts || detail.internal_layouts.length === 0" class="text--secondary mb-0">
                No internal layouts.
              </p>
              <v-card
                  v-for="layout in detail.internal_layouts"
                  :key="'il-' + layout.id"
                  outlined
                  class="pa-4 mb-4"
              >
                <div class="text-body-1 font-weight-medium mb-2">{{ layout.title || "—" }}</div>
                <v-simple-table dense class="mb-3">
                  <tbody>
                  <tr v-for="row in layoutSummaryRows(layout)" :key="row.label">
                    <td width="30%"><strong>{{ row.label }}</strong></td>
                    <td>{{ row.value }}</td>
                  </tr>
                  </tbody>
                </v-simple-table>
                <div v-if="layoutAreaRows(layout).length" class="mb-3">
                  <div class="caption text--secondary mb-1">Areas</div>
                  <v-simple-table dense>
                    <tbody>
                    <tr v-for="row in layoutAreaRows(layout)" :key="row.label">
                      <td width="30%"><strong>{{ row.label }}</strong></td>
                      <td>{{ row.value }}</td>
                    </tr>
                    </tbody>
                  </v-simple-table>
                </div>
                <div v-if="layout.description" class="mb-3">
                  <div class="caption text--secondary mb-1">Description</div>
                  <div class="pa-3" style="border: 1px solid #e0e0e0; border-radius: 4px;" v-html="sanitizeRichHtml(layout.description)"></div>
                </div>
                <div v-if="layout.features && layout.features.length" class="mb-3">
                  <div class="caption text--secondary mb-1">Features</div>
                  <v-simple-table dense>
                    <thead>
                    <tr>
                      <th>Title</th>
                      <th>Floor</th>
                      <th>Width</th>
                      <th>Length</th>
                      <th>Total Area</th>
                    </tr>
                    </thead>
                    <tbody>
                    <tr v-for="feature in layout.features" :key="feature.id">
                      <td>{{ feature.title || "—" }}</td>
                      <td>{{ feature.floor || "—" }}</td>
                      <td>{{ formatDim(feature.width, feature.width_unit) }}</td>
                      <td>{{ formatDim(feature.length, feature.length_unit) }}</td>
                      <td>{{ feature.total_area || "—" }}</td>
                    </tr>
                    </tbody>
                  </v-simple-table>
                </div>
                <div v-if="layout.links && layout.links.length" class="mb-3">
                  <div class="caption text--secondary mb-1">Links</div>
                  <v-simple-table dense>
                    <tbody>
                    <tr v-for="link in layout.links" :key="link.id">
                      <td width="30%"><strong>{{ link.module_title || "Link" }}</strong></td>
                      <td>
                        <div>{{ link.title || "—" }}</div>
                        <a v-if="link.url" :href="link.url" target="_blank" rel="noopener">{{ link.url }}</a>
                      </td>
                    </tr>
                    </tbody>
                  </v-simple-table>
                </div>
                <div class="caption text--secondary mb-2">Files</div>
                <p v-if="!layout.files || layout.files.length === 0" class="text--secondary mb-0">No files.</p>
                <v-row v-else dense>
                  <v-col v-for="file in layout.files" :key="file.id" cols="12" sm="6">
                    <v-card outlined class="pa-3">
                      <div class="d-flex justify-space-between align-center">
                        <div>
                          <div class="text-body-2 font-weight-medium">{{ file.original_name || (file.title + (file.extension || '')) }}</div>
                          <div class="caption text--secondary">{{ file.module_title || "—" }}</div>
                        </div>
                        <v-btn
                            v-if="file.public_url"
                            small
                            color="primary"
                            outlined
                            :href="file.public_url"
                            target="_blank"
                            rel="noopener"
                        >
                          View
                        </v-btn>
                      </div>
                    </v-card>
                  </v-col>
                </v-row>
              </v-card>

              <h3 class="subtitle-1 font-weight-medium mb-3 mt-2">External Layouts</h3>
              <p v-if="!detail.external_layouts || detail.external_layouts.length === 0" class="text--secondary mb-0">
                No external layouts.
              </p>
              <v-card
                  v-for="layout in detail.external_layouts"
                  :key="'el-' + layout.id"
                  outlined
                  class="pa-4 mb-4"
              >
                <div class="text-body-1 font-weight-medium mb-2">{{ layout.title || "—" }}</div>
                <div v-if="layout.description" class="mb-3">
                  <div class="caption text--secondary mb-1">Description</div>
                  <div class="pa-3" style="border: 1px solid #e0e0e0; border-radius: 4px;" v-html="sanitizeRichHtml(layout.description)"></div>
                </div>
                <div class="caption text--secondary mb-2">Files</div>
                <p v-if="!layout.files || layout.files.length === 0" class="text--secondary mb-0">No files.</p>
                <v-row v-else dense>
                  <v-col v-for="file in layout.files" :key="file.id" cols="12" sm="6">
                    <v-card outlined class="pa-3">
                      <div class="d-flex justify-space-between align-center">
                        <div>
                          <div class="text-body-2 font-weight-medium">{{ file.original_name || (file.title + (file.extension || '')) }}</div>
                          <div class="caption text--secondary">{{ file.module_title || "—" }}</div>
                        </div>
                        <v-btn
                            v-if="file.public_url"
                            small
                            color="primary"
                            outlined
                            :href="file.public_url"
                            target="_blank"
                            rel="noopener"
                        >
                          View
                        </v-btn>
                      </div>
                    </v-card>
                  </v-col>
                </v-row>
              </v-card>
            </template>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-btn
              v-if="canApprove"
              color="success"
              dark
              :loading="approving"
              :disabled="detailLoading || rejecting || approving"
              @click="openApproveDialog"
          >
            Approve
          </v-btn>
          <v-btn
              v-if="canReject"
              color="error"
              dark
              class="ml-2"
              :disabled="detailLoading || rejecting || approving"
              @click="openRejectDialog"
          >
            Reject
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn text @click="reviewDialog = false">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="approveDialog" max-width="520px" persistent>
      <v-card>
        <v-card-title>
          Approve Broker Request
          <v-spacer></v-spacer>
          <v-btn icon :disabled="approving" @click="closeApproveDialog">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text>
          <p class="text-body-2 mb-0">
            Approve <strong>{{ requestTitle }}</strong>? This will create the catalog record,
            move uploaded files, and email the broker.
          </p>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text :disabled="approving" @click="closeApproveDialog">Cancel</v-btn>
          <v-btn
              color="success"
              dark
              :loading="approving"
              @click="confirmApprove"
          >
            Approve Request
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="rejectDialog" max-width="520px" persistent>
      <v-card>
        <v-card-title>
          Reject Broker Request
          <v-spacer></v-spacer>
          <v-btn icon :disabled="rejecting" @click="closeRejectDialog">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text>
          <p class="text-body-2 text--secondary mb-4">
            Provide a reason for rejection. The broker will see this on their request.
          </p>
          <v-textarea
              v-model="rejectReason"
              label="Rejection Reason*"
              outlined
              dense
              rows="4"
              auto-grow
              counter="1000"
              maxlength="1000"
              :disabled="rejecting"
              :error-messages="rejectReasonError"
          />
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text :disabled="rejecting" @click="closeRejectDialog">Cancel</v-btn>
          <v-btn
              color="error"
              dark
              :loading="rejecting"
              :disabled="!canSubmitReject"
              @click="confirmReject"
          >
            Reject Request
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import {
  BROKER_REQUESTS_LIST,
  BROKER_REQUEST_DETAIL,
  BROKER_REQUEST_REJECT,
  BROKER_REQUEST_APPROVE,
} from "@/core/services/store/brokerrequests.module";

export default {
  name: "brokers-requests",
  data() {
    return {
      loading: false,
      detailLoading: false,
      suppressOptionsWatch: false,
      pagination: {
        page: 1,
        itemsPerPage: 25,
        sortBy: ["created_at"],
        sortDesc: [true],
      },
      rowsPerPageItems: [25, 50, 100, 150, 200],
      totalRecords: 0,
      searchQuery: "",
      typeFilter: null,
      statusFilter: null,
      typeOptions: [
        {text: "Master Developments", value: "master_development"},
        {text: "Sub Developments", value: "sub_development"},
        {text: "Projects", value: "project"},
      ],
      statusOptions: [
        {text: "Draft", value: "draft"},
        {text: "Under Review", value: "under_review"},
        {text: "Approved", value: "approved"},
        {text: "Rejected", value: "rejected"},
      ],
      headers: [
        {text: "ID", value: "id", sortable: false},
        {text: "Type", value: "request_type_label", sortable: false},
        {text: "Request Type", value: "submission_type_label", sortable: false},
        {text: "Title", value: "title", sortable: false},
        {text: "Broker", value: "broker_name", sortable: false},
        {text: "Agency", value: "broker_agency", sortable: false},
        {text: "Status", value: "status", sortable: false},
        {text: "Submitted", value: "created_at", sortable: false},
        {text: "Actions", value: "actions", sortable: false, align: "center"},
      ],
      items: [],
      reviewDialog: false,
      detail: null,
      rejectDialog: false,
      rejectReason: "",
      rejectReasonError: "",
      rejecting: false,
      approveDialog: false,
      approving: false,
      reviewingItem: null,
      activeChangeTab: null,
    };
  },
  mounted() {
    this.refreshData(true);
  },
  computed: {
    requestTitle() {
      if (!this.detail || !this.detail.request) {
        return (this.reviewingItem && this.reviewingItem.title) || "—";
      }
      return this.detail.request.commercial_title || this.detail.request.title || "—";
    },
    isEditRequest() {
      const type = (this.detail && this.detail.submission_type)
          || (this.reviewingItem && this.reviewingItem.submission_type)
          || "";
      return String(type).toLowerCase() === "edit";
    },
    changePayload() {
      return (this.detail && this.detail.changes) || null;
    },
    changeCount() {
      return (this.changePayload && Number(this.changePayload.change_count)) || 0;
    },
    changedTabs() {
      return (this.changePayload && this.changePayload.changed_tabs) || [];
    },
    changesForActiveTab() {
      const all = (this.changePayload && this.changePayload.changes) || [];
      const tab = this.activeChangeTab || (this.changedTabs[0] && this.changedTabs[0].key);
      if (!tab) {
        return all;
      }
      return all.filter((c) => c.tab === tab);
    },
    detailStatus() {
      const fromDetail = this.detail
          && (this.detail.status
              || (this.detail.request && this.detail.request.status)
              || (this.detail.records && this.detail.records.status));
      if (fromDetail != null && String(fromDetail).trim() !== "") {
        return String(fromDetail).toLowerCase().trim();
      }
      if (this.reviewingItem && this.reviewingItem.status) {
        return String(this.reviewingItem.status).toLowerCase().trim();
      }
      return "";
    },
    detailStatusLabel() {
      if (this.detail && this.detail.status_label) {
        return this.detail.status_label;
      }
      if (this.reviewingItem && this.reviewingItem.status_label) {
        return this.reviewingItem.status_label;
      }
      return this.formatStatus(this.detailStatus);
    },
    canReject() {
      return this.detailStatus === "under_review" || this.detailStatus === "pending";
    },
    canApprove() {
      return this.canReject;
    },
    canSubmitReject() {
      return String(this.rejectReason || "").trim().length >= 3 && !this.rejecting;
    },
    detailRows() {
      const req = this.detail && this.detail.request ? this.detail.request : null;
      if (!req) {
        return [];
      }

      if (this.detail.source === "project") {
        return [
          {label: "Title", value: req.title || "—"},
          {label: "Project No", value: req.project_no || "—"},
          {label: "Plot No", value: req.plot_no || "—"},
          {label: "Configuration", value: req.configuration || "—"},
          {label: "No. of Units", value: req.no_of_units || "—"},
          {label: "No. of Floors", value: req.no_of_floor || "—"},
          {label: "Status %", value: req.status_percentage || "—"},
          {
            label: "Plot Area",
            value: req.plot_area != null ? `${req.plot_area} ${req.plot_area_unit || ""}`.trim() : "—",
          },
          {
            label: "Built-up Area",
            value: req.built_up_area != null ? `${req.built_up_area} ${req.built_up_area_unit || ""}`.trim() : "—",
          },
          {
            label: "Parent Development",
            value: (req.parent_development && req.parent_development.commercial_title) || "—",
          },
          {label: "Latitude", value: req.latitude != null ? req.latitude : "—"},
          {label: "Longitude", value: req.longitude != null ? req.longitude : "—"},
          {label: "Admin Notes", value: req.admin_notes || "—"},
        ];
      }

      return [
        {label: "Commercial Title", value: req.commercial_title || "—"},
        {label: "Development Number", value: req.development_number || "—"},
        {label: "Master Developer(s)", value: this.masterDeveloperLabels},
        {label: "Other Master Developer", value: req.other_master_developer_name || "—"},
        {
          label: "Parent Development",
          value: (req.parent_development && req.parent_development.commercial_title) || "—",
        },
        {label: "Launch Date", value: req.launch_date || "—"},
        {label: "Registration Date", value: req.registration_date || "—"},
        {label: "Construction Started", value: req.construction_started_date || "—"},
        {label: "Anticipated Completion", value: req.anticipated_completion_date || "—"},
        {label: "Handover Date", value: req.handover_date || "—"},
        {label: "Latitude", value: req.latitude != null ? req.latitude : "—"},
        {label: "Longitude", value: req.longitude != null ? req.longitude : "—"},
        {label: "Admin Notes", value: req.admin_notes || "—"},
      ];
    },
    masterDeveloperLabels() {
      const req = this.detail && this.detail.request ? this.detail.request : null;
      if (!req) {
        return "—";
      }
      if (this.detail.master_developer_names && this.detail.master_developer_names.length) {
        return this.detail.master_developer_names.join(", ");
      }
      const ids = Array.isArray(req.contact_ids) ? req.contact_ids : [];
      return ids.length ? ids.map((id) => "#" + id).join(", ") : "—";
    },
  },
  methods: {
    onOptionsUpdate() {
      if (this.suppressOptionsWatch) {
        return;
      }
      this.refreshData(false);
    },
    loadData() {
      this.loading = true;
      return new Promise((resolve) => {
        const params = {
          ...this.pagination,
          length: this.pagination.itemsPerPage,
          start: this.pagination.page === 1 ? 0 : this.pagination.itemsPerPage * (this.pagination.page - 1),
          "search[value]": this.searchQuery || "",
          request_type: this.typeFilter || "",
          status: this.statusFilter || "",
        };

        const query = Object.keys(params).map((key) => {
          return encodeURIComponent(key) + "=" + encodeURIComponent(params[key] == null ? "" : params[key]);
        }).join("&");

        this.$store.dispatch(BROKER_REQUESTS_LIST, {q: query}).then((res) => {
          const recordsRoot = (res && res.records) || {};
          const records = recordsRoot.records || recordsRoot;
          resolve({
            items: records.data || [],
            total: records.recordsFiltered || 0,
          });
          this.loading = false;
        }).catch(() => {
          this.loading = false;
          resolve({items: [], total: 0});
        });
      });
    },
    refreshData(resetPage = false) {
      if (resetPage) {
        this.suppressOptionsWatch = true;
        this.pagination.page = 1;
        this.$nextTick(() => {
          this.suppressOptionsWatch = false;
        });
      }
      this.loadData().then((response) => {
        this.items = response.items;
        this.totalRecords = response.total;
      });
    },
    openReview(item) {
      this.reviewingItem = item || null;
      this.activeChangeTab = null;
      this.reviewDialog = true;
      this.detail = item
          ? {
            source: item.source,
            status: item.status,
            status_label: item.status_label,
            request_type: item.request_type,
            request_type_label: item.request_type_label,
            submission_type: item.submission_type,
            submission_type_label: item.submission_type_label,
            request: {id: item.id, title: item.title, commercial_title: item.title},
            broker: {
              name: item.broker_name,
              email: item.broker_email,
              agency_name: item.broker_agency,
            },
            files: [],
            changes: null,
          }
          : null;
      this.detailLoading = true;
      this.$store
          .dispatch(BROKER_REQUEST_DETAIL, {
            source: item.source,
            id: item.id,
          })
          .then((res) => {
            const payload = this.unwrapDetailPayload(res);
            if (payload) {
              this.detail = payload;
              const tabs = (payload.changes && payload.changes.changed_tabs) || [];
              this.activeChangeTab = tabs.length ? tabs[0].key : null;
            }
          })
          .catch(() => {
            // Keep seeded list-row detail so Approve/Reject still appear for under_review.
          })
          .finally(() => {
            this.detailLoading = false;
          });
    },
    unwrapDetailPayload(res) {
      if (!res || typeof res !== "object") {
        return null;
      }
      let payload = res.records || res.data || res;
      // Handle accidental double-wrap: { records: { status, request, ... } }
      if (payload && payload.records && (payload.records.request || payload.records.status)) {
        payload = payload.records;
      }
      if (!payload || typeof payload !== "object") {
        return null;
      }
      // Normalize status onto the top level for button visibility.
      if (!payload.status && payload.request && payload.request.status) {
        payload.status = payload.request.status;
      }
      if (!payload.status_label && payload.status) {
        payload.status_label = this.formatStatus(payload.status);
      }
      return payload;
    },
    openRejectDialog() {
      this.rejectReason = "";
      this.rejectReasonError = "";
      this.rejectDialog = true;
    },
    closeRejectDialog() {
      if (this.rejecting) return;
      this.rejectDialog = false;
      this.rejectReason = "";
      this.rejectReasonError = "";
    },
    openApproveDialog() {
      this.approveDialog = true;
    },
    closeApproveDialog() {
      if (this.approving) return;
      this.approveDialog = false;
    },
    confirmApprove() {
      if (this.approving) return;
      const source = (this.detail && this.detail.source)
          || (this.reviewingItem && this.reviewingItem.source);
      const id = (this.detail && this.detail.request && this.detail.request.id)
          || (this.reviewingItem && this.reviewingItem.id);
      if (!source || !id) return;
      this.approving = true;
      this.$store
          .dispatch(BROKER_REQUEST_APPROVE, {
            source,
            id,
          })
          .then(() => {
            this.approveDialog = false;
            this.reviewDialog = false;
            this.detail = null;
            this.reviewingItem = null;
            this.refreshData(false);
          })
          .catch(() => {
            /* snackbar handled in store */
          })
          .finally(() => {
            this.approving = false;
          });
    },
    confirmReject() {
      if (!this.canSubmitReject) return;
      const reason = String(this.rejectReason || "").trim();
      if (reason.length < 3) {
        this.rejectReasonError = "Enter at least 3 characters.";
        return;
      }
      const source = (this.detail && this.detail.source)
          || (this.reviewingItem && this.reviewingItem.source);
      const id = (this.detail && this.detail.request && this.detail.request.id)
          || (this.reviewingItem && this.reviewingItem.id);
      if (!source || !id) return;
      this.rejectReasonError = "";
      this.rejecting = true;
      this.$store
          .dispatch(BROKER_REQUEST_REJECT, {
            source,
            id,
            reason: reason,
          })
          .then(() => {
            this.rejectDialog = false;
            this.reviewDialog = false;
            this.detail = null;
            this.reviewingItem = null;
            this.rejectReason = "";
            this.rejectReasonError = "";
            this.refreshData(false);
          })
          .catch(() => {
            /* snackbar handled in store */
          })
          .finally(() => {
            this.rejecting = false;
          });
    },
    formatType(value) {
      const labels = {
        master_development: "Master Developments",
        sub_development: "Sub Developments",
        project: "Projects",
      };
      return labels[value] || value || "—";
    },
    formatStatus(value) {
      const labels = {
        draft: "Draft",
        under_review: "Under Review",
        pending: "Under Review",
        approved: "Approved",
        rejected: "Rejected",
      };
      return labels[String(value || "").toLowerCase()] || value || "—";
    },
    statusColor(value) {
      const key = String(value || "").toLowerCase();
      if (key === "approved") return "green darken-1";
      if (key === "rejected") return "red darken-1";
      if (key === "draft") return "blue-grey";
      return "orange darken-2";
    },
    formatDate(value) {
      if (!value) return "—";
      const d = new Date(value);
      if (Number.isNaN(d.getTime())) return value;
      return d.toLocaleString();
    },
    formatDim(value, unit) {
      if (value == null || value === "") return "—";
      return unit ? `${value} ${unit}` : String(value);
    },
    layoutSummaryRows(layout) {
      if (!layout) return [];
      return [
        {label: "Property Type", value: layout.property_type_title || "—"},
        {label: "Elevation", value: layout.elevation || "—"},
        {label: "Floor Level", value: layout.floor_level || "—"},
        {label: "Bedrooms", value: layout.no_of_bedrooms != null && layout.no_of_bedrooms !== "" ? layout.no_of_bedrooms : "—"},
        {label: "Bathrooms", value: layout.no_of_bathrooms != null && layout.no_of_bathrooms !== "" ? layout.no_of_bathrooms : "—"},
        {label: "Meta Title", value: layout.meta_title || "—"},
        {label: "Meta Keywords", value: layout.meta_keywords || "—"},
        {label: "Focus Keywords", value: layout.focus_keywords || "—"},
        {label: "Meta Description", value: layout.meta_description || "—"},
      ];
    },
    layoutAreaRows(layout) {
      const areas = (layout && layout.areas) || {};
      const labels = {
        min_plot_area: "Min Plot Area",
        max_plot_area: "Max Plot Area",
        min_built_up_area: "Min Built Up Area",
        max_built_up_area: "Max Built Up Area",
        balcony_area: "Balcony Area",
        terrace_area: "Terrace Area",
        suite_area: "Suite Area",
        total_area: "Total Area",
      };
      return Object.keys(labels).map((key) => {
        const area = areas[key];
        if (!area) return null;
        let value = "—";
        if (area.unit === "Dimensions" && (area.length || area.width)) {
          value = `${area.length || "—"} × ${area.width || "—"}`;
        } else if (area.value != null && area.value !== "") {
          value = area.unit ? `${area.value} ${area.unit}` : String(area.value);
        } else if (area.length || area.width) {
          value = `${area.length || "—"} × ${area.width || "—"}${area.unit ? " " + area.unit : ""}`;
        }
        if (value === "—") return null;
        return {label: labels[key], value};
      }).filter(Boolean);
    },
  },
};
</script>
